export default Object.freeze({
    // Base colors:
    transparent: 'transparent',
    white: '#FFFFFF',
    // black: '#000000', - not used (I think)

    // Primary Colors
    // These are the main colors that make up the majority of the colors used in the design system.

    // Cool Gray
    // Cool Gray is the foundational gray of the color system. It is to be used as the primary gray for the UI mainframe background, buttons, borders, and text.
    coolGray25: '#F9FEFF',
    coolGray50: '#F4F9FA',
    coolGray100: '#EEF3F4',
    coolGray200: '#E2E9EB',
    coolGray300: '#D0D9DB',
    coolGray400: '#9DABAE',
    coolGray500: '#68777A',
    coolGray600: '#4F5A5C',
    coolGray700: '#3D4748',
    coolGray800: '#242B2C',
    coolGray900: '#1A1E1F',
    // Neutral Gray
    //  Neutral Gray is to be used within table elements only — primarily for alternating row colors (Neutral Gray 25 & 50 for light mode, and 800 & 900 for dark mode). It provides a warm contrast against Cool Gray in the UI application frame.
    neutralGray25: '#FCFDFD',
    neutralGray50: '#F5F7F8',
    neutralGray100: '#EDEFF0',
    neutralGray200: '#E5E5E5',
    neutralGray300: '#D5D5D5',
    neutralGray400: '#A7ACAD',
    neutralGray500: '#6B7476',
    neutralGray600: '#535B5D',
    neutralGray700: '#393E3E',
    neutralGray800: '#2B2F30',
    neutralGray900: '#232828',
    // Primary
    // Blue is the "primary" UI color, and is used across  interactive elements such as buttons, links, focus states, etc. It’s also used as an accent color in our badge system and dashboard charts.
    primary25: '#F8FBFE',
    primary50: '#E9F5FF',
    primary100: '#D6EDFF',
    primary200: '#C1E3FF',
    primary300: '#97D0FF',
    primary400: '#62B8FE',
    primary500: '#38A2FB',
    primary600: '#1078D0',
    primary700: '#005AB4',
    primary800: '#004A94',
    primary900: '#223348',
    // Red (Accent + Error)
    // Brand Red is used to pull RedBeam branding into the UI (navigational highlight), as well as to display error or delete states. It’s also used as an accent color in our badge system.
    red25: '#FFF9F8',
    red50: '#FFEEEC',
    red100: '#FFE0DD',
    red200: '#FFC5C0',
    red300: '#FF9D94',
    red400: '#FF6E61',
    red500: '#EA3929',
    red600: '#D91B14',
    red700: '#B91D14',
    red800: '#931312',
    red900: '#721816',
    // Orange (Accent + Warning)
    // Warning colors communicate that an action is potentially destructive or "on-hold". These colors are commonly used in confirmations to grab the users' attention. It’s also used as an accent color in our badge system and dashboard charts.
    orange25: '#FFFBF4',
    orange50: '#FFF8EA',
    orange100: '#FEF1D8',
    orange200: '#FFDEAB',
    orange300: '#FFCC7E',
    orange400: '#FFAB52',
    orange500: '#FD911E',
    orange600: '#D97812',
    orange700: '#B54708',
    orange800: '#93370D',
    orange900: '#7A2E0E',
    // Green (Accent + Success)
    // Success colors communicate a positive action, positive trend, or a successful confirmation. It’s also used as an accent color in our badge system and dashboard charts.
    green25: '#EFFFF9',
    green50: '#DEFFF4',
    green100: '#CDF9E9',
    green200: '#98F6D6',
    green300: '#7AEAC3',
    green400: '#50D7A8',
    green500: '#2CBB94',
    green600: '#23997A',
    green700: '#1B765E',
    green800: '#165E4B',
    green900: '#114D3D',

    // Secondary Colors
    // Along with primary colors, it's helpful to have a selection of secondary colors
    // to use in components such as tags, badges, and labels.
    // These secondary colors should be used sparingly or as accents, while the primary color(s) should take precedence.

    // Rose
    rose25: '#FFF8FB',
    rose50: '#FFF3F7',
    rose100: '#FFE7EF',
    rose200: '#F5CBDA',
    rose300: '#E8A3C7',
    rose400: '#DC87B3',
    rose500: '#C2789E',
    rose600: '#A76386',
    rose700: '#814B67',
    rose800: '#5C374A',
    rose900: '#442837',

    // Purple
    purple25: '#FAFAFF',
    purple50: '#F4F3FF',
    purple100: '#EBE9FE',
    purple200: '#D9D6FE',
    purple300: '#BDB4FE',
    purple400: '#8E62FF',
    purple500: '#7B55DB',
    purple600: '#6644BF',
    purple700: '#4F3692',
    purple800: '#3E2A71',
    purple900: '#21163D',

    // Periwinkle
    periwinkle25: '#FCFCFD',
    periwinkle50: '#F8F9FC',
    periwinkle100: '#EAECF5',
    periwinkle200: '#D5D9EB',
    periwinkle300: '#AFB5D9',
    periwinkle400: '#717BBC',
    periwinkle500: '#4E5BA6',
    periwinkle600: '#3E4784',
    periwinkle700: '#363F72',
    periwinkle800: '#293056',
    periwinkle900: '#101323',

    // Teal
    teal25: '#EFFDFF',
    teal50: '#DDFBFF',
    teal100: '#C3F8FF',
    teal200: '#A7EFF8',
    teal300: '#6DE9F9',
    teal400: '#2BD3EA',
    teal500: '#19BCD2',
    teal600: '#19A3B6',
    teal700: '#117B89',
    teal800: '#0F5E68',
    teal900: '#0C4D55',

    // Yellow Green
    yellowGreen25: '#F6FFF3',
    yellowGreen50: '#ECFFE4',
    yellowGreen100: '#D3FFC1',
    yellowGreen200: '#BAF2A3',
    yellowGreen300: '#A2E985',
    yellowGreen400: '#8BD06E',
    yellowGreen500: '#7DB167',
    yellowGreen600: '#679455',
    yellowGreen700: '#517343',
    yellowGreen800: '#3D5933',
    yellowGreen900: '#32482A',

    // Yellow
    yellow25: '#FFFEF5',
    yellow50: '#FFFDE4',
    yellow100: '#FFFAB6',
    yellow200: '#FFE35B',
    yellow300: '#F6CD06',
    yellow400: '#E0BA06',
    yellow500: '#C6A404',
    yellow600: '#A38701',
    yellow700: '#756200',
    yellow800: '#5E4E00',
    yellow900: '#443801'
});
